<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, Layers } from 'lucide-svelte';
  import { adminVerticalsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { avatarColor } from '$lib/utils/display';
  import type { Vertical } from '$lib/types';

  const queryClient = useQueryClient();

  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');

  function emptyForm() {
    return { name: '', slug: '' };
  }
  let form = $state(emptyForm());

  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminVerticalsApi.update(editingId, body) : adminVerticalsApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-verticals'] });
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    showForm = true;
  }

  function openEdit(row: Vertical) {
    editingId = row.id;
    form = { name: row.name, slug: row.slug };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) {
      formError = 'Name and slug are required';
      return;
    }
    formError = '';
    saveMutation.mutate({ name: form.name, slug: form.slug });
  }
</script>

<div class="tym-page">
  <div class="mb-[18px] flex items-center">
    <div>
      <h2 class="tym-section-title">Verticals</h2>
      <div class="tym-section-sub">
        Quote-domain access scoping &middot; slugs must match hotel/builder/export/lighting/retail/dealers to stay
        linked to the quotation vertical enum
      </div>
    </div>
    <span class="flex-1"></span>
    <button type="button" class="btn-primary" onclick={openCreate}>
      <Plus class="h-4 w-4" /> New vertical
    </button>
  </div>

  {#if verticalsQuery.isLoading}
    <p class="tym-loading-state">Loading verticals&hellip;</p>
  {:else if verticalsQuery.isError}
    <p class="tym-error-state">{getErrorMessage(verticalsQuery.error)}</p>
  {:else if verticalsQuery.data}
    <div class="flex max-w-[560px] flex-col gap-2.5">
      {#each verticalsQuery.data.results as row (row.id)}
        <div class="tym-card flex items-center gap-[13px] px-[18px] py-[15px]">
          <span
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] text-white"
            style="background:{avatarColor(row.slug)}"
          >
            <Layers class="h-5 w-5" />
          </span>
          <div class="min-w-0 flex-1">
            <div class="font-heading text-[14.5px] font-bold text-navy">{row.name}</div>
            <div class="truncate text-xs text-dark-gray">{row.slug}</div>
          </div>
          <button type="button" class="btn-secondary !h-9 shrink-0" onclick={() => openEdit(row)}>Rename</button>
        </div>
      {:else}
        <p class="tym-empty-state">No verticals found.</p>
      {/each}
    </div>
  {/if}
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit vertical' : 'New vertical'} onClose={closeForm}>
    <form class="space-y-4" onsubmit={submit}>
      <div>
        <label class="label" for="name">Name</label>
        <input id="name" class="input" bind:value={form.name} />
      </div>
      <div>
        <label class="label" for="slug">Slug</label>
        <input id="slug" class="input" bind:value={form.slug} disabled={!!editingId} />
      </div>

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
